import { useState } from "react";
import { Heart, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { GIVING_FUNDS, IMAGES } from "../lib/data";
import { PageHeader } from "../components/PageHeader";
import { Eyebrow, Field } from "../components/ui-kit";
import { Reveal } from "../lib/motion";
import { api } from "../lib/api";

const PRESETS = [25, 50, 100, 250, 500];

export default function Give() {
  const [fund, setFund] = useState(GIVING_FUNDS[0].id);
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState("monthly");
  const [ppLoading, setPpLoading] = useState(false);

  const finalAmount = custom ? parseFloat(custom) : amount;
  const fundName = GIVING_FUNDS.find((f) => f.id === fund)?.name || fund;

  const payWithPaypal = async () => {
    if (!finalAmount || finalAmount <= 0) { toast.error("Please enter a valid amount."); return; }
    setPpLoading(true);
    try {
      const url = recurring ? "/paypal/donate/subscription" : "/paypal/donate/order";
      const payload = { fund: fundName, amount: finalAmount, origin_url: window.location.origin, name: donor.name, email: donor.email };
      if (recurring) payload.frequency = frequency;
      const { data } = await api.post(url, payload);
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error("Unable to start PayPal. Please try again.");
      setPpLoading(false);
    }
  };

  const give = async () => {
    if (!finalAmount || finalAmount <= 0) { toast.error("Please enter a valid amount."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/stripe/checkout", {
        fund: fundName, amount: finalAmount, origin_url: window.location.origin,
        recurring, frequency, donor_name: donor.name, donor_email: donor.email,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="give-page">
      <PageHeader eyebrow="Generosity" title={"Give"} sub="Your generosity helps One Love Ministries continue spreading love, serving our community, strengthening families, and sharing the message of faith." image={IMAGES.worshipHands} />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-14 lg:gap-20 items-start">
          <Reveal>
            <Eyebrow>An Act of Worship</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05] mb-6">Giving is worship.</h2>
            <p className="text-black/70 text-lg leading-relaxed mb-8">Every gift is an act of faith that fuels ministry, transforms lives, and carries the love of Christ across Little Rock. Thank you for partnering with us.</p>
            <div className="space-y-4">
              {GIVING_FUNDS.map((f) => (
                <div key={f.id} className="flex gap-4 items-start border-b border-black/10 pb-4">
                  <Heart size={18} className="text-[#E63946] mt-1 shrink-0" />
                  <div><p className="font-medium">{f.name}</p><p className="text-black/55 text-sm">{f.desc}</p></div>
                </div>
              ))}
            </div>
            <div className="mt-8 p-5 bg-[#F4F4F5] rounded-sm flex gap-3">
              <ShieldCheck className="text-[#E63946] shrink-0" size={20} />
              <p className="text-sm text-black/60 leading-relaxed">Your gift is processed through a secure, encrypted connection. One Love Ministries never stores your card details, and your personal information is kept private.</p>
            </div>
            <p className="mt-6 text-sm text-black/45 leading-relaxed"><span className="font-medium text-black/60">Other ways to give:</span> Church administrators can add official giving methods (mail-in, in-person, or app-based) here.</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border border-black/10 rounded-sm p-7 sm:p-10">
              <p className="text-xs uppercase tracking-[0.2em] text-black/40 mb-4">Choose a Fund</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {GIVING_FUNDS.map((f) => (
                  <button key={f.id} onClick={() => setFund(f.id)} data-testid={`fund-${f.id}`}
                    className={`px-4 py-2.5 rounded-full text-sm transition-colors ${fund === f.id ? "bg-[#0A0A0A] text-white" : "border border-black/15 hover:border-black/40"}`}>{f.name}</button>
                ))}
              </div>

              <p className="text-xs uppercase tracking-[0.2em] text-black/40 mb-3">Giving Type</p>
              <div className="flex gap-2 mb-6">
                <button onClick={() => setRecurring(false)} data-testid="type-onetime" className={`flex-1 py-3 rounded-sm text-sm transition-colors ${!recurring ? "bg-[#0A0A0A] text-white" : "border border-black/15 hover:border-black/40"}`}>One-Time</button>
                <button onClick={() => setRecurring(true)} data-testid="type-recurring" className={`flex-1 py-3 rounded-sm text-sm transition-colors ${recurring ? "bg-[#0A0A0A] text-white" : "border border-black/15 hover:border-black/40"}`}>Recurring</button>
              </div>
              {recurring && (
                <div className="flex flex-wrap gap-2 mb-6" data-testid="frequency-group">
                  {[["weekly", "Weekly"], ["biweekly", "Bi-weekly"], ["monthly", "Monthly"]].map(([v, l]) => (
                    <button key={v} onClick={() => setFrequency(v)} data-testid={`freq-${v}`} className={`px-4 py-2.5 rounded-full text-sm transition-colors ${frequency === v ? "bg-[#E63946] text-white" : "border border-black/15 hover:border-[#E63946]"}`}>{l}</button>
                  ))}
                </div>
              )}

              <p className="text-xs uppercase tracking-[0.2em] text-black/40 mb-4">Amount</p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {PRESETS.map((p) => (
                  <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`amount-${p}`}
                    className={`py-4 rounded-sm font-display font-bold text-xl transition-colors ${!custom && amount === p ? "bg-[#E63946] text-white" : "border border-black/15 hover:border-[#E63946]"}`}>${p}</button>
                ))}
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40">$</span>
                  <input type="number" min="1" placeholder="Other" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="amount-custom"
                    className="w-full h-full pl-8 pr-3 rounded-sm border border-black/15 focus:border-[#E63946] outline-none font-display font-bold text-lg" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 my-8">
                <Field label="Name (optional)" name="name" value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })} />
                <Field label="Email (optional)" name="email" type="email" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })} />
              </div>

              <button onClick={give} disabled={loading} data-testid="give-now-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] text-white py-5 text-base font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-50">
                {loading ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : <>{recurring ? `Give $${finalAmount || 0} / ${frequency} with Card` : `Give $${finalAmount || 0} with Card`} <Heart size={16} /></>}
              </button>
              <button onClick={payWithPaypal} disabled={ppLoading} data-testid="give-paypal-btn"
                className="w-full mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white py-5 text-base font-medium hover:bg-[#001c4d] transition-colors disabled:opacity-60">
                {ppLoading ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : <><span className="font-bold italic">PayPal</span> — {recurring ? `Give $${finalAmount || 0} / ${frequency}` : `Give $${finalAmount || 0}`}</>}
              </button>
              <p className="text-center text-xs text-black/40 mt-4">Card checkout by Stripe includes Google Pay &amp; Apple Pay · PayPal also available · Secure &amp; encrypted</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
